export type PreviewSession<S extends object> = {
  // Shows a change on the map without saving it or adding an undo step.
  // `compute` always receives the style from before the session started, so
  // repeated previews (a drag) never build on each other.
  preview(compute: (base: S) => S): void;
  // Makes the change last previewed permanent: one save, one undo step.
  commit(): void;
  // Puts the map back to how it was before the session started.
  cancel(): void;
  // A change with no preview phase: one save, one undo step.
  apply(compute: (current: S) => S): void;
  // The style without any pending preview.
  original(): S;
  // True only while a change of this session is pending. False again once it
  // was committed, cancelled, or ended from outside (redo, another style
  // opened).
  isPreviewing(): boolean;
  // True for every style this session produced (previewed, committed or
  // applied) during its lifetime. Anything else came from outside.
  owns(style: S): boolean;
};

type SessionOptions<S> = {
  // The style the app currently shows.
  getStyle(): S;
  onPreview(style: S): void;
  onCommit(style: S): void;
};

type Pending<S> = {
  base: S;
  // The last style previewed: what a commit makes permanent.
  shown: S;
  // Every style previewed for this change.
  emitted: Set<S>;
  // True once one of them was seen on screen.
  seen: boolean;
};

// Owns "a change in progress". The app stores styles by reference, so
// identity tells whose style is on screen.
//
// A pending change is live while the screen shows one of its own previews,
// or still shows the base because no preview has been rendered yet (two
// events without a render in between). It ends silently, without
// committing, as soon as anything else is on screen: another style, or the
// base put back from outside after a preview had been shown.
export function createPreviewSession<S extends object>(
  options: SessionOptions<S>
): PreviewSession<S> {
  let pending: Pending<S> | null = null;
  const owned = new WeakSet<S>();

  const live = (): Pending<S> | null => {
    if (!pending) return null;
    const current = options.getStyle();
    if (pending.emitted.has(current)) {
      pending.seen = true;
      return pending;
    }
    if (current === pending.base && !pending.seen) return pending;
    pending = null;
    return null;
  };

  return {
    preview(compute) {
      const session = live();
      const base = session ? session.base : options.getStyle();
      const shown = compute(base);
      if (shown !== base) owned.add(shown);
      pending = {
        base,
        shown,
        emitted: session ? session.emitted.add(shown) : new Set([shown]),
        seen: session ? session.seen : false,
      };
      options.onPreview(shown);
    },
    commit() {
      const session = live();
      pending = null;
      if (session && session.shown !== session.base) {
        options.onCommit(session.shown);
      }
    },
    cancel() {
      const session = live();
      pending = null;
      if (session) options.onPreview(session.base);
    },
    apply(compute) {
      const session = live();
      pending = null;
      const current = session ? session.base : options.getStyle();
      const next = compute(current);
      if (next !== current) {
        owned.add(next);
        options.onCommit(next);
      } else if (session) {
        options.onPreview(session.base);
      }
    },
    original() {
      const session = live();
      return session ? session.base : options.getStyle();
    },
    isPreviewing() {
      return live() !== null;
    },
    owns(style) {
      return owned.has(style);
    },
  };
}
